import { useCallback, useEffect, useMemo, useState } from "react";
import { City, State } from "country-state-city";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from "../services/apiHelpers";

/* ───────────────────────── Data & helpers (unchanged logic) ───────────────────────── */

const INDIAN_STATES = State.getStatesOfCountry("IN");
const STATE_NAMES = INDIAN_STATES.map((state) => state.name);
const getStateByName = (stateName) => INDIAN_STATES.find((state) => state.name === stateName);
const getCitiesForState = (stateName) => {
  const state = getStateByName(stateName);
  return state ? City.getCitiesOfState("IN", state.isoCode).map((city) => city.name) : [];
};

const CLINIC_TYPES = ["Private Clinic", "Government Clinic", "Multi-specialty Clinic", "Polyclinic", "Diagnostic Center"];
const CODES = ["+91", "+1", "+44", "+61", "+971", "+65"];
const MAX_ADDRESS = 200;

const normalizeContactNumber = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return { code: "+91", phone: "" };
  const match = raw.match(/^(\+\d{1,4})\s*(.*)$/);
  if (match) return { code: match[1], phone: match[2].replace(/\s+/g, " ").trim() };
  return { code: "+91", phone: raw.replace(/\s+/g, " ").trim() };
};

const buildProfilePayload = (currentForm) => {
  const contactNumber = [currentForm.code, currentForm.phone].filter(Boolean).join(" ").trim();
  return {
    clinicName: currentForm.clinicName,
    clinicType: currentForm.clinicType,
    registrationNumber: currentForm.registrationNumber,
    city: currentForm.city,
    stateName: currentForm.stateName,
    stateCode: currentForm.stateCode || getStateByName(currentForm.stateName)?.isoCode || "",
    pincode: currentForm.pincode,
    contactNumber,
    email: currentForm.email,
    addressLine: currentForm.addressLine,
  };
};

const mapServerProfile = (data = {}) => {
  const contact = normalizeContactNumber(data.contact_number || data.contactNumber || "");
  const stateName = data.state_name || data.stateName || "Puducherry";
  const stateCode = data.state_code || data.stateCode || getStateByName(stateName)?.isoCode || "PY";
  return {
    clinicName: data.clinic_name || data.clinicName || "",
    code: contact.code,
    phone: contact.phone,
    email: data.email || "",
    registrationNumber: data.registration_number || data.registrationNumber || "",
    clinicType: data.clinic_type || data.clinicType || "Private Clinic",
    addressLine: data.address_line || data.addressLine || "",
    stateName,
    stateCode,
    city: data.city || "",
    pincode: data.pincode || "",
  };
};

/* ───────────────────────── Icons ───────────────────────── */

const ICONS = {
  building: <><path d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16" /><path d="M14 9h5a1 1 0 0 1 1 1v11" /><path d="M2 21h20M8 8h2M8 12h2M8 16h2M17 13h1M17 17h1" /></>,
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  shield: <path d="M12 3 4 6v6c0 4.5 3.2 8.2 8 9 4.8-.8 8-4.5 8-9V6zM12 8v5M12 16h.01" />,
  pin: <><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></>,
  hash: <path d="M5 9h14M5 15h14M10 4 8 20M16 4l-2 16" />,
  chevron: <path d="m6 9 6 6 6-6" />,
  save: <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></>,
  clinic: <><rect x="5" y="3" width="14" height="18" rx="1.5" /><path d="M12 8v4M10 10h4M10 21v-4h4v4" /></>,
  check: <path d="m5 12 5 5L20 7" />,
  alert: <><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></>,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
};

const Icon = ({ name, size = 18, className = "" }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

/* ───────────────────────── UI building blocks ───────────────────────── */

function Field({ label, required, htmlFor, children, fullWidth = false }) {
  return (
    <div className={`cs-field${fullWidth ? " cs-field-full" : ""}`}>
      <label className="cs-label" htmlFor={htmlFor}>
        {label}
        {required && <span className="cs-req" aria-hidden="true"> *</span>}
      </label>
      {children}
    </div>
  );
}

function InputBox({ icon, tall, children }) {
  return (
    <div className={`cs-box ${tall ? "cs-box-tall" : ""}`}>
      <span className="cs-adorn"><Icon name={icon} size={17} /></span>
      {children}
    </div>
  );
}

function SelectBox({ id, value, onChange, options, icon }) {
  return (
    <InputBox icon={icon}>
      <select id={id} className="cs-control cs-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
      <Icon name="chevron" size={15} className="cs-caret" />
    </InputBox>
  );
}

/* ───────────────────────── Page ───────────────────────── */

export default function ClinicSettings() {
  const { auth } = useAuth();
  const clinicId = auth?.clinic?.id ?? (typeof window !== "undefined" ? Number(window.__CLINIC_ID__ || localStorage.getItem("clinicId") || 0) : 0);

  const [otpRequired, setOtpRequired] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [status, setStatus] = useState({ message: "", tone: "info" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [form, setForm] = useState({
    clinicName: "",
    code: "+91",
    phone: "",
    email: "",
    registrationNumber: "",
    clinicType: "Private Clinic",
    addressLine: "",
    stateName: "Puducherry",
    stateCode: "PY",
    city: "",
    pincode: "",
  });

  const notify = (message, tone = "info") => setStatus({ message, tone });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  // auto-dismiss success messages
  useEffect(() => {
    if (status.tone !== "success" || !status.message) return undefined;
    const t = setTimeout(() => setStatus({ message: "", tone: "info" }), 4000);
    return () => clearTimeout(t);
  }, [status]);

  const fetchClinicProfile = useCallback(async () => {
    if (!clinicId) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(buildApiUrl(`settings/clinic-profile/${clinicId}`), {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Failed to load clinic profile");
      const mapped = mapServerProfile(data);
      setForm(mapped);
      setBaseline(mapped);
      notify("");
    } catch (error) {
      console.error("Failed to load clinic profile:", error);
      notify(error.message || "Unable to load clinic profile", "error");
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    fetchClinicProfile();
  }, [fetchClinicProfile]);

  const verifyOtpAndSave = async (payload) => {
    if (!clinicId) {
      notify("Clinic ID is missing. Please refresh and try again.", "error");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(buildApiUrl("settings/verify-profile-update"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, clinicId, otp: otpValue }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) throw new Error(data.message || "OTP verification failed");

      setOtpRequired(false);
      setOtpValue("");
      setBaseline(form);
      notify(data.message || "Profile updated successfully", "success");
    } catch (error) {
      console.error("OTP verification failed:", error);
      notify(error.message || "OTP verification failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!clinicId) {
      notify("Clinic not found. Please sign in again.", "error");
      return;
    }
    const payload = buildProfilePayload(form);
    setSaving(true);
    try {
      const response = await fetch(buildApiUrl(`settings/clinic-profile/${clinicId}`), {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || "Unable to update clinic profile");

      if (data.otpRequired) {
        setOtpRequired(true);
        notify(data.message || "OTP sent to your registered email", "info");
        return;
      }
      setOtpRequired(false);
      setOtpValue("");
      setBaseline(form);
      notify(data.message || "Clinic profile updated", "success");
    } catch (error) {
      console.error("Unable to update clinic profile:", error);
      notify(error.message || "Unable to update clinic profile", "error");
    } finally {
      setSaving(false);
    }
  };

  /* derived UI state */
  const dirty = useMemo(() => !!baseline && JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);
  const cityOptions = useMemo(() => {
    const cities = getCitiesForState(form.stateName);
    return form.city && !cities.includes(form.city) ? [form.city, ...cities] : cities;
  }, [form.stateName, form.city]);

  return (
    <div className="cs-page">
      <style>{css}</style>

      <nav className="cs-crumbs" aria-label="Breadcrumb">
        <span>Settings</span>
        <Icon name="chevron" size={11} className="cs-crumb-sep" />
        <span className="cs-crumb-current">Clinic Settings</span>
      </nav>

      <header className="cs-head">
        <h1>Clinic Settings</h1>
        <p>Manage your clinic details and contact information.</p>
      </header>

      <div className="cs-card">
        <div className="cs-card-accent" aria-hidden="true" />
        <form onSubmit={otpRequired ? (e) => { e.preventDefault(); verifyOtpAndSave(buildProfilePayload(form)); } : save}>
          <div className="cs-card-head">
            <span className="cs-card-icon"><Icon name="clinic" size={18} /></span>
            <div>
              <h2>Clinic Information</h2>
              <p>These details will be used across appointments, invoices and patient communication.</p>
            </div>
          </div>

          {status.message && (
            <div className={`cs-banner cs-${status.tone}`} role={status.tone === "error" ? "alert" : "status"}>
              <span className="cs-banner-icon"><Icon name={status.tone === "error" ? "alert" : status.tone === "success" ? "check" : "mail"} size={15} /></span>
              <span className="cs-banner-text">{status.message}</span>
              <button type="button" className="cs-banner-x" aria-label="Dismiss message" onClick={() => notify("")}>
                <Icon name="close" size={14} />
              </button>
            </div>
          )}

          <div className="cs-grid">
            <Field label="Clinic Name" required htmlFor="cs-name">
              {loading ? <div className="cs-skel" /> : (
                <InputBox icon="building">
                  <input id="cs-name" className="cs-control" value={form.clinicName} onChange={onInput("clinicName")} required />
                </InputBox>
              )}
            </Field>

            <Field label="Clinic Type" required htmlFor="cs-type">
              {loading ? <div className="cs-skel" /> : (
                <SelectBox id="cs-type" icon="clinic" value={form.clinicType} onChange={set("clinicType")} options={CLINIC_TYPES} />
              )}
            </Field>

            <Field label="Registration / License Number (Optional)" htmlFor="cs-reg">
              {loading ? <div className="cs-skel" /> : (
                <InputBox icon="shield">
                  <input id="cs-reg" className="cs-control" value={form.registrationNumber} onChange={onInput("registrationNumber")} />
                </InputBox>
              )}
            </Field>

            <Field label="Pincode" required htmlFor="cs-pin">
              {loading ? <div className="cs-skel" /> : (
                <InputBox icon="hash">
                  <input id="cs-pin" className="cs-control" inputMode="numeric" value={form.pincode} onChange={onInput("pincode")} required />
                </InputBox>
              )}
            </Field>

            <Field label="Phone Number" required htmlFor="cs-phone">
              {loading ? <div className="cs-skel" /> : (
                <div className="cs-box">
                  <span className="cs-adorn"><Icon name="phone" size={17} /></span>
                  <div className="cs-code">
                    <select className="cs-control cs-select" value={form.code} onChange={onInput("code")} aria-label="Country code">
                      {CODES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <Icon name="chevron" size={15} className="cs-caret" />
                  </div>
                  <input id="cs-phone" className="cs-control" type="tel" value={form.phone} onChange={onInput("phone")} required />
                </div>
              )}
            </Field>

            <Field label="Email Address" required htmlFor="cs-email">
              {loading ? <div className="cs-skel" /> : (
                <InputBox icon="mail">
                  <input id="cs-email" className="cs-control" type="email" value={form.email} onChange={onInput("email")} required />
                </InputBox>
              )}
            </Field>

            <Field label="Address" required htmlFor="cs-address" fullWidth>
              {loading ? <div className="cs-skel cs-skel-tall" /> : (
                <InputBox icon="pin" tall>
                  <textarea id="cs-address" className="cs-control cs-textarea" rows={2} maxLength={MAX_ADDRESS} value={form.addressLine} onChange={onInput("addressLine")} required />
                  <span className="cs-count">{(form.addressLine || "").length}/{MAX_ADDRESS}</span>
                </InputBox>
              )}
            </Field>

            <Field label="State" required htmlFor="cs-state">
              {loading ? <div className="cs-skel" /> : (
                <SelectBox id="cs-state" icon="pin" value={form.stateName} onChange={(value) => setForm((f) => ({ ...f, stateName: value, stateCode: getStateByName(value)?.isoCode || "", city: "" }))} options={STATE_NAMES} />
              )}
            </Field>

            <Field label="City" required htmlFor="cs-city">
              {loading ? <div className="cs-skel" /> : (
                <SelectBox id="cs-city" icon="pin" value={form.city} onChange={set("city")} options={cityOptions} />
              )}
            </Field>
          </div>

          {otpRequired && (
            <div className="cs-otp">
              <div className="cs-otp-icon"><Icon name="lock" size={19} /></div>
              <div className="cs-otp-body">
                <h4>Verify this change</h4>
                <p>Enter the one-time code sent to your registered email to confirm the update.</p>
                <div className="cs-otp-row">
                  <input
                    className="cs-otp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Enter OTP"
                    aria-label="One-time password"
                    value={otpValue}
                    onChange={(e) => setOtpValue(e.target.value)}
                  />
                  <button type="button" className="cs-link" onClick={() => save({ preventDefault() {} })} disabled={saving}>Resend code</button>
                </div>
              </div>
            </div>
          )}

          <div className="cs-footer">
            <span className={`cs-dirty ${dirty || otpRequired ? "on" : ""}`}>
              <i />{otpRequired ? "Waiting for verification" : dirty ? "You have unsaved changes" : "All changes saved"}
            </span>
            <button type="submit" className="cs-save" disabled={loading || saving || (!otpRequired && !dirty)}>
              {saving ? <span className="cs-spin" /> : <Icon name="save" size={17} />}
              {saving ? "Saving…" : otpRequired ? "Verify & Save" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
      <div className="cs-bottom-space" aria-hidden="true" />
    </div>
  );
}

/* ───────────────────────── Styles ───────────────────────── */

const css = `
.cs-page{
  --ink:#0b1c33; --ink-2:#1c3050; --muted:#64748b; --faint:#98a3ba;
  --line:#e6eaf2; --line-2:#d3dae7; --paper:#fff; --card:#fff;
  --accent-1:#0e7c9c; --accent-2:#1451c4; --accent-soft:#e8f1fb;
  --red:#c0392b; --red-soft:#fbeceb;
  --green:#0f8a5f; --green-soft:#e3f6ee;
  --amber:#a15c00; --amber-soft:#fdf1de;
  font-family:'Inter','Segoe UI',system-ui,-apple-system,sans-serif;
  color:var(--ink); min-height:100%; box-sizing:border-box; padding:30px 44px 120px;
  background:transparent;
}
.cs-page *{box-sizing:border-box}
.cs-page>*{max-width:1060px;margin-left:auto;margin-right:auto}

.cs-crumbs{display:flex;align-items:center;gap:7px;font-size:13px;color:var(--muted);letter-spacing:.01em}
.cs-crumb-sep{opacity:.55}
.cs-crumb-current{color:var(--ink);font-weight:600}

.cs-head{margin-top:12px;margin-bottom:22px}
.cs-head h1{margin:0 0 7px;font-size:29px;line-height:1.15;font-weight:800;letter-spacing:-.015em}
.cs-head p{margin:0;font-size:15px;color:var(--muted)}

.cs-card{position:relative;margin-bottom:0;background:var(--card);border-radius:16px;border:1px solid var(--line);overflow:hidden;
  box-shadow:0 1px 2px rgba(16,32,80,.04),0 18px 38px -30px rgba(16,32,80,.16)}
.cs-bottom-space{height:120px;min-height:120px;flex:0 0 120px}
.cs-card-accent{position:absolute;top:0;left:0;right:0;height:4px;
  background:linear-gradient(90deg,var(--accent-1),var(--accent-2))}

.cs-card-head{display:flex;gap:14px;padding:30px 32px 22px}
.cs-card-icon{flex:0 0 38px;height:38px;border-radius:11px;display:grid;place-items:center;
  background:var(--accent-soft);color:var(--accent-2)}
.cs-card-head h2{margin:0 0 4px;font-size:18.5px;font-weight:700;letter-spacing:-.008em}
.cs-card-head p{margin:0;color:var(--muted);font-size:14px;max-width:56ch}

.cs-banner{display:flex;align-items:center;gap:11px;margin:0 32px 22px;padding:11px 12px 11px 13px;border-radius:12px;font-size:13.5px;font-weight:500;border:1px solid}
.cs-banner-icon{flex:0 0 22px;height:22px;border-radius:50%;display:grid;place-items:center;background:rgba(255,255,255,.55)}
.cs-banner-text{flex:1}
.cs-banner-x{border:0;background:transparent;color:inherit;opacity:.6;cursor:pointer;display:grid;place-items:center;padding:4px;border-radius:8px;flex:0 0 auto}
.cs-banner-x:hover{opacity:1;background:rgba(0,0,0,.06)}
.cs-success{background:var(--green-soft);border-color:#bfe4d3;color:var(--green)}
.cs-error{background:var(--red-soft);border-color:#f0c9c6;color:var(--red)}
.cs-info{background:var(--amber-soft);border-color:#ecd9b3;color:var(--amber)}

.cs-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:22px 24px;padding:0 32px 6px;align-items:start}
.cs-field{display:flex;flex-direction:column;min-width:0}
.cs-field-full{grid-column:1 / -1}
.cs-label{font-size:13px;font-weight:600;margin-bottom:8px;color:var(--ink-2)}
.cs-req{color:var(--red)}

.cs-box{position:relative;display:flex;align-items:stretch;height:46px;background:#fff;
  border:1.5px solid var(--line);border-radius:12px;transition:border-color .15s,box-shadow .15s;overflow:hidden}
.cs-box:hover{border-color:var(--line-2)}
.cs-box:focus-within{border-color:var(--accent-2);box-shadow:0 0 0 4px rgba(20,81,196,.12)}
.cs-box-tall{height:auto;min-height:74px}
.cs-adorn{flex:0 0 42px;display:flex;align-items:center;justify-content:center;color:var(--faint);transition:color .15s}
.cs-box:focus-within>.cs-adorn{color:var(--accent-2)}
.cs-box-tall .cs-adorn{align-items:flex-start;padding-top:13px}
.cs-control{flex:1;min-width:0;border:0;outline:0;background:transparent;font:inherit;font-size:14.5px;font-weight:500;color:var(--ink);padding:0 13px 0 0}
.cs-control::placeholder{color:var(--faint);font-weight:400}
.cs-textarea{padding:13px 13px 22px 0;resize:none;line-height:1.45;font-weight:500;min-height:74px}
.cs-count{position:absolute;right:13px;bottom:8px;font-size:11px;color:var(--faint);pointer-events:none}
.cs-select{appearance:none;-webkit-appearance:none;cursor:pointer;padding-right:36px}
.cs-caret{position:absolute;right:13px;top:50%;transform:translateY(-50%);pointer-events:none;color:var(--faint)}
.cs-code{position:relative;flex:0 0 86px;border-right:1.5px solid var(--line);margin-right:13px}
.cs-code .cs-select{width:100%;height:100%;padding-left:2px}

.cs-skel{height:46px;border-radius:12px;background:linear-gradient(100deg,#eef1f7 30%,#f8f9fc 50%,#eef1f7 70%);background-size:220% 100%;animation:cs-sh 1.3s linear infinite}
.cs-skel-tall{min-height:74px}
@keyframes cs-sh{to{background-position:-220% 0}}

.cs-otp{display:flex;gap:15px;padding:18px 20px;margin:22px 32px 0;border-radius:14px;
  background:linear-gradient(135deg,var(--amber-soft),#fff);border:1px solid #ecd9b3}
.cs-otp-icon{flex:0 0 42px;height:42px;border-radius:12px;display:grid;place-items:center;color:#fff;
  background:linear-gradient(145deg,#c98d1f,var(--amber));box-shadow:0 8px 16px -6px rgba(161,92,0,.45)}
.cs-otp-body{flex:1;min-width:0}
.cs-otp-body h4{margin:0 0 3px;font-size:14.5px;font-weight:700}
.cs-otp-body p{margin:0 0 12px;font-size:13px;color:var(--muted)}
.cs-otp-row{display:flex;align-items:center;gap:14px;flex-wrap:wrap}
.cs-otp-input{width:200px;height:44px;padding:0 14px;font:inherit;font-size:16px;font-weight:700;letter-spacing:.3em;color:var(--ink);
  background:#fff;border:1.5px solid var(--line-2);border-radius:11px;outline:0;transition:border-color .15s,box-shadow .15s}
.cs-otp-input::placeholder{letter-spacing:normal;font-size:13.5px;font-weight:500;color:var(--faint)}
.cs-otp-input:focus{border-color:var(--amber);box-shadow:0 0 0 4px rgba(161,92,0,.15)}
.cs-link{border:0;background:none;padding:6px 2px;font:inherit;font-size:13px;font-weight:600;color:var(--accent-2);cursor:pointer}
.cs-link:hover:not(:disabled){text-decoration:underline}
.cs-link:disabled{opacity:.5;cursor:default}

.cs-footer{display:flex;align-items:center;justify-content:space-between;gap:16px;
  margin-top:26px;padding:18px 32px;border-top:1px solid var(--line);background:#fafbfd}
.cs-dirty{display:inline-flex;align-items:center;gap:9px;font-size:13px;font-weight:500;color:var(--muted)}
.cs-dirty i{width:7px;height:7px;border-radius:50%;background:var(--green);box-shadow:0 0 0 4px rgba(15,138,95,.15)}
.cs-dirty.on i{background:var(--amber);box-shadow:0 0 0 4px rgba(161,92,0,.16)}

.cs-save{display:inline-flex;align-items:center;gap:8px;height:46px;padding:0 24px;border-radius:12px;border:0;
  font:inherit;font-size:14.5px;font-weight:700;color:#fff;cursor:pointer;
  background:linear-gradient(135deg,var(--accent-1),var(--accent-2));
  box-shadow:0 10px 22px -8px rgba(20,81,196,.55),inset 0 1px 0 rgba(255,255,255,.16);
  transition:transform .15s,box-shadow .15s,filter .15s}
.cs-save:hover:not(:disabled){transform:translateY(-1px);filter:brightness(1.05)}
.cs-save:active:not(:disabled){transform:translateY(0)}
.cs-save:disabled{cursor:not-allowed;color:var(--muted);background:var(--line);box-shadow:none}
.cs-save:focus-visible{outline:3px solid rgba(20,81,196,.35);outline-offset:2px}
.cs-spin{width:15px;height:15px;border-radius:50%;border:2.5px solid rgba(255,255,255,.4);border-top-color:#fff;animation:cs-rot .7s linear infinite}
@keyframes cs-rot{to{transform:rotate(360deg)}}

@media (max-width:720px){
  .cs-page{padding:22px 18px 96px}
  .cs-card{margin-bottom:0}
  .cs-bottom-space{height:88px;min-height:88px;flex-basis:88px}
  .cs-grid{grid-template-columns:1fr;padding:0 20px 4px;gap:18px}
  .cs-card-head{padding:24px 20px 18px}
  .cs-banner{margin:0 20px 20px}
  .cs-otp{margin:20px 20px 0}
  .cs-footer{padding:16px 20px;flex-direction:column;align-items:stretch}
  .cs-save{justify-content:center}
  .cs-otp-input{width:100%}
}
@media (prefers-reduced-motion:reduce){
  .cs-page *{transition:none!important;animation-duration:.01ms!important}
}
`;